'use client';

import { useUser } from '@clerk/nextjs';
import { useEffect, useRef } from 'react';
import { CALENDAR_READONLY_SCOPE } from '@/code/core/services/availability/calendarScopes';
import { useCalendarReauthorize } from '@/code/frontend/components/hooks/useCalendarReauthorize';
import { system } from '@/code/lib/system-logger';
import { trpc } from '@/code/lib/utils/trpc';

// How long to wait between silent re-grant attempts for a given user. A revoked
// (not merely clobbered) grant can't be healed; this window stops a redirect
// loop where reauthorize keeps firing on every load without ever succeeding.
const AUTOHEAL_THROTTLE_MS = 60 * 60 * 1000; // 1 hour

// `liveReadOk` rarely flips, so a long staleTime avoids re-probing Google on
// every client navigation.
const STATUS_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

function throttleKey(userId: string): string {
  return `gcal-autoheal-${userId}`;
}

/**
 * Reads the most recent self-heal attempt timestamp from localStorage. Returns
 * 0 (never attempted) on any parse/access failure — localStorage can throw in
 * private-mode/SSR-ish contexts and a read failure must not block a heal.
 */
function lastAttempt(userId: string): number {
  try {
    const raw = window.localStorage.getItem(throttleKey(userId));
    if (!raw) return 0;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function markAttempt(userId: string): void {
  try {
    window.localStorage.setItem(throttleKey(userId), String(Date.now()));
  } catch {
    // If we can't persist the throttle we MUST NOT redirect — otherwise the
    // heal would loop. The caller guards on this returning successfully.
    throw new Error('Failed to persist auto-heal throttle');
  }
}

/**
 * Silent Google Calendar scope self-heal.
 *
 * Google is the sign-in provider, and Clerk re-mints the Google token with only
 * app-level scopes on each login — clobbering a user's per-user
 * `calendar.readonly` grant so live reads 403 even though Clerk still advertises
 * the scope. For users who opted into calendar matching, this provider silently
 * re-authorizes on load (Google remembers the prior grant, so no consent screen
 * appears) to restore the scope.
 *
 * Effect-only — renders null. Strictly gated to avoid redirect loops:
 *   - Only runs for users whose Clerk publicMetadata records the opt-in
 *     (`additionalScopes` includes calendar.readonly). Non-opted-in users are
 *     never touched.
 *   - Only acts on an EXPLICIT `liveReadOk === false` (never null/loading/true).
 *   - localStorage throttle (1h) keyed per user, set BEFORE redirecting, so a
 *     revoked grant that can't be healed can't loop.
 */
export function CalendarScopeAutoHeal() {
  const { user, isLoaded } = useUser();
  const reauthorize = useCalendarReauthorize();
  // Guard against firing twice within a single mount (e.g. effect re-run while
  // the redirect is in flight).
  const firedRef = useRef(false);

  const additionalScopes = Array.isArray(user?.publicMetadata?.additionalScopes)
    ? (user.publicMetadata.additionalScopes as unknown[]).filter(
        (scope): scope is string => typeof scope === 'string',
      )
    : [];
  const optedIn = additionalScopes.includes(CALENDAR_READONLY_SCOPE);

  // Only probe Google for opted-in, signed-in users — never touch the rest.
  const statusQuery = trpc.user.googleCalendarStatus.useQuery(undefined, {
    enabled: isLoaded && !!user && optedIn,
    staleTime: STATUS_STALE_TIME_MS,
  });

  const liveReadOk = statusQuery.data?.liveReadOk;
  const userId = user?.id;

  // A healthy read means we're not in a loop — clear the throttle so the NEXT
  // clobber (e.g. another login an hour later) can heal immediately. The
  // throttle only needs to survive while reauthorize keeps FAILING (liveReadOk
  // stays false, e.g. a revoked grant) to break a redirect loop.
  useEffect(() => {
    if (liveReadOk === true && userId) {
      try {
        window.localStorage.removeItem(throttleKey(userId));
      } catch {
        // best-effort
      }
    }
  }, [liveReadOk, userId]);

  useEffect(() => {
    if (!optedIn || !userId) return;
    // ONLY the explicit false — null/undefined/true/loading must not trigger.
    if (liveReadOk !== false) return;
    if (firedRef.current) return;

    // Throttle: a revoked grant can't be healed, so cap retries to once/hour to
    // prevent an endless reauthorize redirect loop.
    if (Date.now() - lastAttempt(userId) < AUTOHEAL_THROTTLE_MS) return;

    firedRef.current = true;

    void (async () => {
      try {
        // Visible marker so the silent heal is observable (vs a login that
        // happened to be fine). Also recorded in localStorage as the throttle
        // key `gcal-autoheal-<userId>` — its timestamp proves the heal fired.
        system.info(
          '[calendar-autoheal] opted-in calendar token clobbered — firing silent reauthorize',
          { userId },
        );
        // Set the throttle BEFORE redirecting. If persisting fails we abort the
        // heal entirely — redirecting without a throttle risks a loop.
        markAttempt(userId);

        const redirectUrl = await reauthorize([CALENDAR_READONLY_SCOPE]);
        // User cancelled the verification modal, or Google returned no URL —
        // either way, do nothing (the throttle already blocks the next attempt).
        if (!redirectUrl) return;

        window.location.href = redirectUrl;
      } catch (err) {
        system.error('Calendar scope auto-heal failed', err);
      }
    })();
  }, [optedIn, userId, liveReadOk, reauthorize]);

  return null;
}
